import * as React from 'react';
import { useMemo } from "react";
import { ArrowLeft } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import TableTemplate from "../../components/common/TableTemplate";
import { generateDynamicColumns } from "../../utils/generateDynamicColumns";
import LoadingState from "../../components/states/LoadingState";
import ErrorState from "../../components/states/ErrorState";
import { CampaignAdStatsContainer } from "../../components/campaigns/CampaignAdStatsContainer";
import { useGetCampaignAdsQuery } from "../../services/campaign";
import { columnHelper } from "../../utils/utils";

export function CampaignsPage() {
  const { id: productId, campaignId } = useParams();
  const navigate = useNavigate();

  const {
    data: campaignAds,
    isLoading,
    error,
    isError,
  } = useGetCampaignAdsQuery(
    {
      page: "Campaign.Ad",
      glid: campaignId,
    },
    {
      // Skip query if we don't have a campaignId
      skip: !campaignId,
    }
  );

  const columns = useMemo(() => {
    if (campaignAds && campaignAds[1] && campaignAds[1].Column_header) {
      return generateDynamicColumns({
        calledBy: "campaigns",
        columnHelper,
        headerData: campaignAds[1].Column_header,
        navigationConfig: {
          stats: {
            navigable: true,
            getUrl: ({ originalRow }) => {
              const adGlid = originalRow.glid.display;
              return {
                pathname: `/product/${productId}/campaign/${campaignId}/ad/${adGlid}`,
                state: null,
              };
            },
          },
        },
        editableFields: {
          status: {
            inputType: "select",
          },
        },
      });
    }
    return [];
  }, [campaignAds, productId, campaignId]);

  // Placeholder campaign data
  const placeholderCampaignData = {
    columns: [
      { header: "Campaign Name", accessorKey: "name" },
      { header: "Status", accessorKey: "status" },
      { header: "Budget", accessorKey: "budget" },
    ],
    data: [
      { id: 1, name: "Sample Campaign", status: "Active", budget: "$1000" },
    ],
  };

  if (isError) {
    return <ErrorState error={error?.message || "An error occurred"} />;
  }

  if (isLoading || !campaignAds) {
    return <LoadingState />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate(`/product/${productId}`)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8"
      >
        <ArrowLeft className="h-5 w-5" />
        <span>Back to Product</span>
      </button>

      <div
        className="p-2 mb-4"
        style={{
          backgroundColor: campaignAds[0]?.Color,
        }}
      >
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          {campaignAds[0]?.title}
        </h1>
        <h2 className="text-2xl font-medium text-gray-800/75">
          {campaignAds[0]?.["sub-title"]}
        </h2>
      </div>

      {/* Campaign Overview Table */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">
            Campaign Overview (Fake placeholder until we get the data)
          </h3>
          <TableTemplate
            columns={placeholderCampaignData.columns}
            data={placeholderCampaignData.data}
            options={{
              enablePagination: false,
              enableColumnFilters: false,
              showPreferences: false,
            }}
          />
        </div>
      </div>

      {/* Tabbed Content */}
      <div className="bg-white rounded-lg shadow">
        <Tabs defaultValue="ads" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="ads">Campaign Ads</TabsTrigger>
            <TabsTrigger value="analytics">Stats</TabsTrigger>
          </TabsList>

          <TabsContent value="ads" className="p-4">
            <TableTemplate
              columns={columns}
              data={campaignAds[1]?.data || []}
              options={{
                defaultPageSize: 50,
                enableColumnFilters: true,
                enableColumnOrder: true,
                enableColumnResize: true,
                enableColumnVisibility: true,
                enableGlobalFilter: true,
                enablePagination: true,
                enableSorting: true,
                showPreferences: true,
                showTotalItems: true,
              }}
            />
          </TabsContent>

          <TabsContent value="analytics" className="p-4">
            <CampaignAdStatsContainer />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default CampaignsPage;
